/**
 * Orders page configuration — status groups and action buttons.
 *
 * Pure data: no React imports, no side effects. Maps FSM states to
 * visual groups and defines which bulk actions are available per group.
 */

// ---------------------------------------------------------------------------
// Status Groups
// ---------------------------------------------------------------------------

export type StatusGroup = {
  key: string
  labelKey: string
  icon: string
  color: string
  states: string[]
}

export const statusGroups: StatusGroup[] = [
  {
    key: "inPreparation",
    labelKey: "ordersPage.groups.inPreparation",
    icon: "Build",
    color: "#2196f3",
    states: ["CREATED", "ORDER_CREATED", "IN_PREPARATION"],
  },
  {
    key: "readyForHaulage",
    labelKey: "ordersPage.groups.readyForHaulage",
    icon: "Inventory",
    color: "#4caf50",
    states: ["READY_FOR_HAULAGE"],
  },
  {
    key: "onTheWay",
    labelKey: "ordersPage.groups.onTheWay",
    icon: "LocalShipping",
    color: "#ff9800",
    states: ["ON_THE_WAY_TO_CUSTOMER"],
  },
  {
    key: "completed",
    labelKey: "ordersPage.groups.completed",
    icon: "CheckCircle",
    color: "#66bb6a",
    states: ["DELIVERED", "COMPLETED"],
  },
  {
    key: "backToStore",
    labelKey: "ordersPage.groups.backToStore",
    icon: "Store",
    color: "#9c27b0",
    states: ["ON_THE_WAY_TO_PROVIDER"],
  },
  {
    key: "actionRequired",
    labelKey: "ordersPage.groups.actionRequired",
    icon: "Warning",
    color: "#f44336",
    states: [
      "TO_PROCESS_PROVIDER",
      "TO_PROCESS_PROVIDER_CLIENT",
      "TO_PROCESS_PROVIDER_RECURRING",
      "TO_PROCESS_PROVIDER_ERROR",
      "TO_PROCESS_PROVIDER_DELIVERED",
    ],
  },
  {
    key: "cancelled",
    labelKey: "ordersPage.groups.cancelled",
    icon: "Cancel",
    color: "#757575",
    states: ["CANCELLED"],
  },
]

/** Reverse lookup: state string → group key */
export const stateToGroupKey: Record<string, string> = {}
for (const group of statusGroups) {
  for (const state of group.states) {
    stateToGroupKey[state] = group.key
  }
}

// ---------------------------------------------------------------------------
// Action Buttons
// ---------------------------------------------------------------------------

export type ActionButton = {
  key: string
  labelKey: string
  event: string
  multiSelect: boolean
  visibleForGroups: string[]
  color: "primary" | "success" | "error" | "warning"
}

export const actionButtons: ActionButton[] = [
  {
    key: "sendToPreparation",
    labelKey: "ordersPage.actions.sendToPreparation",
    event: "ORDER_PREPARATION",
    multiSelect: true,
    visibleForGroups: ["inPreparation", "actionRequired"],
    color: "primary",
  },
  {
    key: "sendToDelivery",
    labelKey: "ordersPage.actions.sendToDelivery",
    event: "ORDER_PREPARED",
    multiSelect: true,
    visibleForGroups: ["inPreparation", "actionRequired", "cancelled"],
    color: "primary",
  },
  {
    key: "delivered",
    labelKey: "ordersPage.actions.delivered",
    event: "PROVIDER_DELIVERY",
    multiSelect: true,
    visibleForGroups: ["readyForHaulage"],
    color: "success",
  },
  {
    key: "cancel",
    labelKey: "ordersPage.actions.cancel",
    event: "CANCELLATION",
    multiSelect: true,
    visibleForGroups: [
      "inPreparation",
      "readyForHaulage",
      "onTheWay",
      "actionRequired",
    ],
    color: "error",
  },
]
